import type { ReactNode } from 'react';

import { Button } from 'components/Button/Button';
import { Divider } from 'components/Divider/Divider';
import { Form } from 'components/Form/Form';
import type { FormSubmitFunction } from 'components/Form/FormContext';
import { InputField } from 'components/Form/InputField';
import { Icon } from 'components/Icon/Icon';
import { LottieAnimation } from 'components/LottieAnimation/LottieAnimation';
import { expiredListingLottieAnimationData } from 'components/LottieAnimation/lottieAnimationsData';
import { H3 } from 'components/Text/H3';
import { HeadingSmall } from 'components/Text/HeadingSmall';
import { VisuallyHidden } from 'components/VisuallyHidden/VisuallyHidden';
import { SearchContextHeader } from 'modules/search/components/Context/SearchContextHeader';
import type { SearchContextHeaderData } from 'modules/search/containers/SearchContextHeader/SearchContextHeaderData';

import {
  UnpublishedListingExploreSection,
  UnpublishedListingHolder,
  UnpublishedListingSearchContextHeaderHolder,
  UnpublishedListingUpperSection,
  UnpublishedListingUpperSectionAnimation,
  UnpublishedListingUpperSectionAnimationHolder,
  UnpublishedListingUpperSectionContent,
  UnpublishedListingUpperSectionFooter,
  UnpublishedListingUpperSectionFooterSearchField,
  UnpublishedListingUpperSectionHeader,
  UnpublishedListingUpperSectionHeaderTitle,
  UnpublishedListingUpperSectionSeeMore,
} from './UnpublishedListing.styled';

type UnpublishedListingSearchValues = { query: string };

type Props = {
  searchContext: SearchContextHeaderData | undefined;
  footerTitle: string;
  seeMoreText: ReactNode;
  searchFormAnalyticsTitle: string;
  onSubmit: FormSubmitFunction<UnpublishedListingSearchValues>;
  exploreSection?: ReactNode;
};

export function UnpublishedListing({
  searchContext,
  footerTitle,
  seeMoreText,
  exploreSection,
  searchFormAnalyticsTitle,
  onSubmit,
}: Props) {
  return (
    <UnpublishedListingHolder>
      {searchContext && (
        <UnpublishedListingSearchContextHeaderHolder>
          <SearchContextHeader searchContext={searchContext} />
        </UnpublishedListingSearchContextHeaderHolder>
      )}

      <UnpublishedListingUpperSection>
        <UnpublishedListingUpperSectionAnimation>
          <UnpublishedListingUpperSectionAnimationHolder>
            <LottieAnimation
              data={expiredListingLottieAnimationData}
              size={undefined}
            />
          </UnpublishedListingUpperSectionAnimationHolder>
        </UnpublishedListingUpperSectionAnimation>

        <UnpublishedListingUpperSectionContent>
          <UnpublishedListingUpperSectionHeader>
            <HeadingSmall>{getText('Unpublished listing')}</HeadingSmall>

            <UnpublishedListingUpperSectionHeaderTitle>
              {getText('Whoops! This listing has been unpublished.')}
            </UnpublishedListingUpperSectionHeaderTitle>
          </UnpublishedListingUpperSectionHeader>

          <UnpublishedListingUpperSectionSeeMore>
            {seeMoreText}
          </UnpublishedListingUpperSectionSeeMore>

          <UnpublishedListingUpperSectionFooter>
            <H3>{footerTitle}</H3>

            <Form
              analyticsTitle={searchFormAnalyticsTitle}
              usageContext="page"
              onSubmit={onSubmit}
              initialValues={
                { query: '' } satisfies UnpublishedListingSearchValues
              }
            >
              <UnpublishedListingUpperSectionFooterSearchField>
                <InputField
                  id="unpublished-listing-query"
                  placeholder={getText('Search by keyword, skill, or interest')}
                  name="query"
                  noMargin
                />

                <Button type="submit">
                  <Icon name="search" size={16} />
                  <VisuallyHidden>{getText('Search')}</VisuallyHidden>
                </Button>
              </UnpublishedListingUpperSectionFooterSearchField>
            </Form>
          </UnpublishedListingUpperSectionFooter>
        </UnpublishedListingUpperSectionContent>
      </UnpublishedListingUpperSection>

      {exploreSection && (
        <>
          <Divider marginTop={16} marginBottom={16} />

          <UnpublishedListingExploreSection>
            {exploreSection}
          </UnpublishedListingExploreSection>
        </>
      )}
    </UnpublishedListingHolder>
  );
}
